/**
 * Where the Most Valued Player award has got to, from three dates and a clock.
 *
 * The league runs the prize in two rounds — squads name a nominee each, then
 * every player ranks three of them — and each round has a published closing
 * time. This turns those dates into the one answer every surface needs: is the
 * ballot accepting votes right now, and what should the page say if not.
 *
 * DERIVED, NOT STORED. An `is_open` flag beside the dates is a second source of
 * truth and eventually disagrees with them — the flag says open, the deadline
 * says Friday, and nobody can say which is right. There is no flag; there are
 * dates and a clock.
 *
 * PURE, AND THE CLOCK IS AN ARGUMENT. "Has Friday 9pm been and gone" is the
 * whole of this module, and a function that reads the clock itself cannot be
 * tested for the minute either side of a deadline.
 */

/**
 * South African Standard Time, as a fixed offset — the same constant the
 * team-sheet embargo uses, and for the same reason: the country has kept no
 * daylight saving since 1944, so "9pm on Friday" is one instant all year.
 */
export const SAST_OFFSET_MINUTES = 120;

/**
 * A `datetime-local` value, read as the LEAGUE'S clock rather than the
 * browser's.
 *
 * The organiser may set a deadline from anywhere; a player may read it from
 * anywhere. "Friday 9pm" has to mean Friday 9pm in Johannesburg either way,
 * and a datetime-local input hands back a bare wall-clock string with no zone
 * at all — passing that to `new Date()` silently attaches whichever zone the
 * machine happens to be in, which is how a deadline moves two hours without
 * anybody touching it.
 */
export function sastInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const utc = Date.UTC(+y, +mo - 1, +d, +h, +mi) - SAST_OFFSET_MINUTES * 60_000;
  const at = new Date(utc);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The inverse, for filling the form back in with what was saved. */
export function isoToSastInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const local = new Date(at.getTime() + SAST_OFFSET_MINUTES * 60_000);
  return local.toISOString().slice(0, 16);
}

export interface MvpDates {
  /** When squads must have named their nominee. Null for no published date. */
  nominationsClose: Date | null;
  /** When the ballot starts taking votes. Null means it never opens. */
  votingOpens: Date | null;
  /** When it stops. Null means open until a closing time is set. */
  votingCloses: Date | null;
}

export type MvpPhase =
  /** No ballot is planned for this season. */
  | 'none'
  /** Squads are still naming their nominees. */
  | 'nominating'
  /** Nominees are in; the ballot has a start time that has not arrived. */
  | 'before'
  /** Votes are being taken. */
  | 'voting'
  /** The ballot has closed. */
  | 'closed';

export interface MvpState {
  phase: MvpPhase;
  /** Whether a ballot may be cast this instant. The server checks this too. */
  open: boolean;
  /** The next moment that changes the answer, for a countdown. Null if none. */
  nextAt: Date | null;
}

/**
 * The state of the award at `now`.
 *
 * Order matters and it is the order a reader experiences: a ballot with no
 * opening date is not "closed", it is a vote that has not been arranged.
 */
export function mvpState(dates: MvpDates, nominees: number, now: Date): MvpState {
  const { nominationsClose, votingOpens, votingCloses } = dates;
  const at = now.getTime();

  // Shut, and shut for good, whatever else the dates say. A closing time in
  // the past outranks an opening time in the past — otherwise a vote that ran
  // last month still reads as live.
  if (votingCloses && at >= votingCloses.getTime()) {
    return { phase: 'closed', open: false, nextAt: null };
  }

  if (votingOpens && at >= votingOpens.getTime()) {
    return { phase: 'voting', open: true, nextAt: votingCloses ?? null };
  }

  // Before the ballot opens there are two different things to say, and which
  // one depends on whether the squads still have time to nominate.
  if (nominationsClose && at < nominationsClose.getTime()) {
    return { phase: 'nominating', open: false, nextAt: nominationsClose };
  }

  if (votingOpens) {
    return { phase: 'before', open: false, nextAt: votingOpens };
  }

  // No opening date at all. A season with nominees and no date is one whose
  // organiser has not arranged the vote yet; a season with neither is one that
  // is not holding the prize.
  return { phase: nominees > 0 ? 'nominating' : 'none', open: false, nextAt: nominationsClose };
}

/**
 * "3 days", "4 hours", "12 minutes" — how long until `at`.
 *
 * Coarse on purpose. A deadline four days away does not need a second hand,
 * and a countdown that ticks invites a reader to watch it rather than vote.
 * Null once the moment has passed, so a caller cannot print "in -2 hours".
 */
export function timeUntil(at: Date | null, now: Date): string | null {
  if (!at) return null;
  const ms = at.getTime() - now.getTime();
  if (ms <= 0) return null;

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * Whether the tab bar should carry a way into the ballot.
 *
 * HERE RATHER THAN IN THE TAB BAR, so the rule and its test cannot drift apart
 * from what the app actually draws. Stating it inline in App.tsx would leave a
 * test asserting one rule while the bar obeyed another.
 *
 * True for every phase but 'none'. Note both ends:
 *
 *   - A season with neither nominees nor dates is not holding the prize, and
 *     the three leagues that do not run it must not grow a tab for it.
 *   - A CLOSED ballot keeps its tab. Dropping it at the final whistle would
 *     take the winner's name off the app on the one evening everybody looks.
 */
export function ballotHasTab(dates: MvpDates, nominees: number, now: Date): boolean {
  return mvpState(dates, nominees, now).phase !== 'none';
}

/**
 * Why a set of dates cannot be saved, or null when they are fine.
 *
 * WHAT THIS EXISTS FOR, because it is a real afternoon lost. The organiser set
 * the ballot up with the closing time four days BEFORE the opening time. Every
 * field was a valid date, so every field saved, and `mvpState` did exactly what
 * it should: a closing time in the past outranks everything, so the ballot read
 * `closed`. Permanently. Six nominees in, 62 players on the roll, and a vote
 * that could never open — with nothing on the screen saying anything was wrong,
 * because from the app's point of view nothing was.
 *
 * The phase logic is right and stays as it is. The place to catch this is the
 * save, which until now checked only that each value WAS a date and never that
 * the three made sense together.
 *
 * Returns the sentence the organiser reads, not a code: she is the one who has
 * to fix it, and "votingCloses must be after votingOpens" is not her language.
 */
export function rejectDates(dates: MvpDates): string | null {
  const { votingOpens, votingCloses } = dates;
  if (!votingOpens || !votingCloses) return null;

  if (votingCloses.getTime() <= votingOpens.getTime()) {
    return (
      'Voting closes before it opens, so the ballot could never take a vote. ' +
      'Set the closing time after the opening time.'
    );
  }
  return null;
}
